/**
 * Thuysvers Subscription Quit Modal
 *
 * Opens when the subscription-quit-button is clicked.
 * - Cancel button: clears cart and redirects to cancel_subscription_button_url
 * - Continue button: closes modal; optional redirect to continue_subscription_button_url
 * - Close button/backdrop: closes the modal
 */

const ROOT_ATTR = '[data-tv-subscription-quit-modal]';
const OPEN_ATTR = '[data-tv-quit-open]';
const DISMISS_ATTR = '[data-tv-quit-dismiss]';
const CHOICE_ATTR = '[data-tv-quit-choice]';

const cartClearUrl =
  typeof Theme !== 'undefined' && Theme?.routes?.cart_url
    ? `${Theme.routes.cart_url.replace(/\/?$/, '')}/clear.js`
    : '/cart/clear.js';

const SUBSCRIPTION_SESSION_KEYS = [
  'thuysvers_subscription_survey_state',
  'tv_subscription_active_step_handle',
  'tv_subscription_oneoff_enabled',
];

function clearSubscriptionSessionStorage() {
  try {
    SUBSCRIPTION_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

export class TvSubscriptionQuitModalController {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    /** @type {HTMLElement} */
    this.root = root;
    /** @type {HTMLElement | null} */
    this.dismissEl = root.querySelector(DISMISS_ATTR);
    /** @type {NodeListOf<Element>} */
    this.dismissEls = root.querySelectorAll(DISMISS_ATTR);
    /** @type {boolean} */
    this.bound = false;
  }

  bind() {
    if (this.bound) return;
    this.bound = true;

    this.dismissEls.forEach((el) => {
      el.addEventListener('click', () => this.dismiss());
    });

    this.root.addEventListener('click', (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      const el = target.closest(CHOICE_ATTR);
      if (!(el instanceof HTMLElement)) return;

      const choice = String(el.getAttribute('data-tv-quit-choice') || '').trim();
      if (choice === 'cancel') {
        e.preventDefault();
        this.handleCancel();
        return;
      }
      if (choice === 'continue') {
        e.preventDefault();
        const url = String(this.root.dataset.continueUrl || '').trim();
        this.close();
        if (url) {
          window.location.href = url;
        }
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (!this.isOpen()) return;
      this.dismiss();
    });

    const openBtn = document.querySelector(OPEN_ATTR);
    if (openBtn instanceof HTMLElement) {
      openBtn.addEventListener('click', () => this.open());
    }

    const isEditor =
      typeof window.Shopify !== 'undefined' &&
      Boolean(window.Shopify.designMode || window.Shopify.visualPreviewMode);
    if (this.root.dataset.tvQuitAutoOpen === 'true' && !isEditor) {
      queueMicrotask(() => this.open());
    }
  }

  isOpen() {
    return !this.root.hidden && !this.root.classList.contains('tv-quit-modal--hidden');
  }

  open() {
    this.root.hidden = false;
    this.root.classList.remove('tv-quit-modal--hidden');
    this.root.setAttribute('aria-hidden', 'false');

    const focusTarget =
      this.root.querySelector('.tv-quit-modal__btn') ||
      this.root.querySelector('.tv-quit-modal__dialog');
    if (focusTarget instanceof HTMLElement) {
      try {
        focusTarget.focus();
      } catch {
        // ignore
      }
    }
    return true;
  }

  close() {
    this.root.classList.add('tv-quit-modal--hidden');
    this.root.setAttribute('aria-hidden', 'true');
    this.root.hidden = true;
    return true;
  }

  dismiss() {
    this.close();
    return true;
  }

  async handleCancel() {
    const url = this.root.dataset.cancelUrl || '';
    this.close();
    clearSubscriptionSessionStorage();

    try {
      const res = await fetch(cartClearUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      if (!res.ok) {
        console.warn('[TvSubscriptionQuitModal] Failed to clear cart:', res.status);
      }
    } catch (err) {
      console.warn('[TvSubscriptionQuitModal] Error clearing cart:', err);
    }

    if (url) {
      window.location.href = url;
    } else {
      window.location.reload();
    }
  }
}

/** @type {WeakMap<HTMLElement, TvSubscriptionQuitModalController>} */
const quitModalControllers = new WeakMap();

/**
 * @param {HTMLElement} root
 * @returns {TvSubscriptionQuitModalController}
 */
function getController(root) {
  let controller = quitModalControllers.get(root);
  if (!controller) {
    controller = new TvSubscriptionQuitModalController(root);
    quitModalControllers.set(root, controller);
  }
  return controller;
}

/** @type {TvSubscriptionQuitModalController | null} */
let _instance = null;

/**
 * @returns {TvSubscriptionQuitModalController | null}
 */
export function getTvSubscriptionQuitModal() {
  const root = document.querySelector(ROOT_ATTR);
  if (!(root instanceof HTMLElement)) return null;
  if (!_instance || _instance.root !== root) _instance = getController(root);
  _instance.bind();
  return _instance;
}

export const TvSubscriptionQuitModal = {
  get: getTvSubscriptionQuitModal,
  open: () => getTvSubscriptionQuitModal()?.open() ?? false,
  close: () => getTvSubscriptionQuitModal()?.close() ?? false,
  dismiss: () => getTvSubscriptionQuitModal()?.dismiss() ?? false,
  isOpen: () => getTvSubscriptionQuitModal()?.isOpen() ?? false,
};

try {
  /** @type {any} */ (window).TvSubscriptionQuitModal = TvSubscriptionQuitModal;
} catch {
  // ignore
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => getTvSubscriptionQuitModal());
} else {
  getTvSubscriptionQuitModal();
}
